import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { useAuthStore } from './stores/authStore';
import ErrorBoundary from './components/ui/ErrorBoundary';

// Layout
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import WelcomeDashboard from './pages/WelcomeDashboard';
import Import from './pages/Import';
import PartsListPage from './pages/PartsListPage';
import PartDetail from './pages/PartDetail';
import ProgramsIndex from './pages/ProgramsIndex';
import ProgramDetail from './pages/ProgramDetail';
import ScenarioComparison from './pages/ScenarioComparison';
import ChargebackGenerator from './pages/ChargebackGenerator';
import TariffRates from './pages/TariffRates';
import PipelineBoard from './pages/PipelineBoard';
import Goals from './pages/Goals';
import Settings from './pages/Settings';
import IHSDataManagement from './pages/IHSDataManagement';
import PlanningHub from './pages/PlanningHub';
import ClaimsHub from './pages/ClaimsHub';
import ClaimCreate from './pages/ClaimCreate';
import ClaimDetail from './pages/ClaimDetail';
import ClaimsDashboard from './pages/ClaimsDashboard';
import CompetitiveIntelligencePage from './components/competitive-intelligence/CompetitiveIntelligencePage';
import AlertsPage from './pages/AlertsPage';
import AIChatPage from './pages/AIChatPage';
import AIChatWidget from './components/ai/AIChatWidget';
import WorkflowsPage from './pages/WorkflowsPage';
import MyTasksPage from './pages/MyTasksPage';
import WorkflowAnalyticsDashboard from './pages/WorkflowAnalyticsDashboard';
import MonitoringDashboard from './pages/admin/MonitoringDashboard';
import RegressionDashboard from './pages/admin/RegressionDashboard';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  const checkAuth = useAuthStore((state) => state.checkAuth);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/dashboard" element={<ErrorBoundary><WelcomeDashboard /></ErrorBoundary>} />
                <Route path="/old-dashboard" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
                <Route path="/import" element={<ErrorBoundary><Import /></ErrorBoundary>} />
                <Route path="/opportunities" element={<ErrorBoundary><PartsListPage /></ErrorBoundary>} />
                <Route path="/programs" element={<ErrorBoundary><ProgramsIndex /></ErrorBoundary>} />
                <Route path="/pipeline" element={<ErrorBoundary><PipelineBoard /></ErrorBoundary>} />
                <Route path="/planning" element={<ErrorBoundary><PlanningHub /></ErrorBoundary>} />
                <Route path="/goals" element={<ErrorBoundary><Goals /></ErrorBoundary>} />
                <Route path="/claims" element={<ErrorBoundary><ClaimsHub /></ErrorBoundary>} />
                <Route path="/claims/new" element={<ErrorBoundary><ClaimCreate /></ErrorBoundary>} />
                <Route path="/claims/dashboard" element={<ErrorBoundary><ClaimsDashboard /></ErrorBoundary>} />
                <Route path="/claims/:claimId" element={<ErrorBoundary><ClaimDetail /></ErrorBoundary>} />
                <Route path="/parts/:partId" element={<ErrorBoundary><PartDetail /></ErrorBoundary>} />
                <Route path="/parts/:partId/compare" element={<ErrorBoundary><ScenarioComparison /></ErrorBoundary>} />
                <Route path="/programs/:programId" element={<ErrorBoundary><ProgramDetail /></ErrorBoundary>} />
                <Route path="/chargebacks/new" element={<ErrorBoundary><ChargebackGenerator /></ErrorBoundary>} />
                <Route path="/tariff-rates" element={<ErrorBoundary><TariffRates /></ErrorBoundary>} />
                <Route path="/materials" element={<ComingSoon page="Materials" />} />
                <Route path="/volume-management" element={<ErrorBoundary><IHSDataManagement /></ErrorBoundary>} />
                <Route path="/competitive-intelligence" element={<ErrorBoundary><CompetitiveIntelligencePage /></ErrorBoundary>} />
                <Route path="/alerts" element={<ErrorBoundary><AlertsPage /></ErrorBoundary>} />
                <Route path="/workflows" element={<ErrorBoundary><WorkflowsPage /></ErrorBoundary>} />
                <Route path="/workflows/analytics" element={<ErrorBoundary><WorkflowAnalyticsDashboard /></ErrorBoundary>} />
                <Route path="/my-tasks" element={<ErrorBoundary><MyTasksPage /></ErrorBoundary>} />
                <Route path="/admin/monitoring" element={<ErrorBoundary><MonitoringDashboard /></ErrorBoundary>} />
                <Route path="/admin/regression" element={<ErrorBoundary><RegressionDashboard /></ErrorBoundary>} />
                <Route path="/settings" element={<ErrorBoundary><Settings /></ErrorBoundary>} />
                <Route path="/flo" element={<ErrorBoundary><AIChatPage /></ErrorBoundary>} />
              </Route>
            </Route>

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <AIChatWidget />
        </BrowserRouter>
        <Toaster />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// Coming Soon placeholder component
function ComingSoon({ page }) {
  return (
    <div className="flex items-center justify-center h-96">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{page}</h2>
        <p className="text-gray-600">This page is coming soon in Phase 3</p>
      </div>
    </div>
  );
}

export default App;
